import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import Pagination from 'components/ui/pagination';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

import LoadingSpinner from 'components/ui/loading_spinner';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';

import {
  downloadLeasesAsCSV,
  downloadSalesAsCSV,
  retrieveLeases,
  retrieveSales
} from './store/actions/search';
import { changeLeasePage, changeSalePage } from './store/actions/query';

import CompsMapContainer from './comps_map_container';
import CompsMapList from './comps_map_list';
import CompsPageHeader from './header';
import LeasesTable from './lease/leases_table';
import SalesTable from './sale/sales_table';
import CompsFilterTopbar from './filters/comps_filter_topbar';
import CompsDeleteSavedSearch from './modals/delete_saved_search_modal';
import { COMP_VIEW_MAP, COMP_VIEW_LIST, COMP_VIEWS } from './comp_constants';
import { canExport, getCurrentTab, getCurrentView } from 'bundles/comps/index/store/selectors/ui';
import { getSelectedLeaseIds, getSelectedSaleIds } from 'bundles/comps/index/store/selectors/select_list';
import { Col, Row } from 'react-bootstrap-5';

export class CompsTab extends React.Component {
  static propTypes = {
    canExport: PropTypes.bool.isRequired,
    currentTab: PropTypes.oneOf(['lease', 'sale']).isRequired,
    currentView: PropTypes.oneOf(COMP_VIEWS).isRequired,
    downloadLeasesAsCSV: PropTypes.func.isRequired,
    downloadSalesAsCSV: PropTypes.func.isRequired,
    edgeCrm: PropTypes.bool,
    isFetching: PropTypes.bool.isRequired,
    leases: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
    retrieveLeases: PropTypes.func.isRequired,
    retrieveSales: PropTypes.func.isRequired,
    sales: PropTypes.array.isRequired,
    selectedLeaseIds: PropTypes.array,
    selectedSaleIds: PropTypes.array,
    onChangeLeasePage: PropTypes.func.isRequired,
    onChangeSalePage: PropTypes.func.isRequired
  };

  scrollPanel = React.createRef();

  scrollToTop() {
    this.scrollPanel.current.scrollTop = 0;
  }

  sale = () => this.props.currentTab === 'sale';

  selectedIds = () => {
    return this.sale() ? this.props.selectedSaleIds : this.props.selectedLeaseIds;
  }

  handlePageChange = (page) => {
    const { currentTab, onChangeLeasePage, onChangeSalePage, retrieveLeases, retrieveSales } = this.props;
    if (currentTab === 'lease') {
      onChangeLeasePage(page);
      retrieveLeases();
    } else {
      onChangeSalePage(page);
      retrieveSales();
    }

    this.scrollToTop();
  };

  render() {
    const { edgeCrm } = this.props;

    return (
      <div className={classNames('h-100', { 'bg-body-secondary p-4': edgeCrm })}>
        <div
          className={classNames('d-flex flex-column h-100', {
            'bg-white border rounded-3 overflow-hidden': edgeCrm
          })}
        >
          <CompsPageHeader />
          {this.renderFilterTopBar()}
          <div className="d-flex flex-column flex-grow-1 overflow-hidden" ref={this.scrollPanel}>
            {this.renderActiveTab()}
          </div>
          <CompsDeleteSavedSearch />
        </div>
      </div>
    );
  }

  renderActiveTab() {
    const { currentView, edgeCrm } = this.props;

    switch (currentView) {
    case COMP_VIEW_LIST:
      return (
        <>
          {this.renderResults()}
          {this.renderPagination()}
        </>
      );
    case COMP_VIEW_MAP:
      return (
        <div className={classNames('maps-container d-flex h-100', { 'px-3 pb-3': edgeCrm })}>
          <Row className={classNames('flex-grow-1', edgeCrm ? 'gx-3 gy-0' : 'g-0')}>
            <Col className="d-flex flex-column h-100" md={4}>
              <CompsMapList onPageChange={this.handlePageChange} />
            </Col>

            <Col className="h-100" md={8}>
              <div
                className={classNames('maps-view w-100 h-100 position-relative', {
                  'rounded-3 border overflow-hidden': edgeCrm
                })}
              >
                <CompsMapContainer name="maps" />
              </div>
            </Col>
          </Row>
        </div>
      );
    }
  }

  renderNoResults() {
    return (
      <div className="m-10">
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faSearch} />
          </Empty.Media>
          <Empty.Title>No results found</Empty.Title>
          <Empty.Content>Search with different criteria or create a New Comp</Empty.Content>
        </Empty>
      </div>
    );
  }

  renderResults() {
    const { isFetching, currentTab } = this.props;

    if (isFetching) {
      return <LoadingSpinner className="flex-fill" />;
    }

    switch (currentTab) {
    case 'lease':
      return this.renderLeases();
    case 'sale':
      return this.renderSales();
    }
  }

  renderLeases() {
    const { leases } = this.props;
    if (leases.length === 0) {
      return this.renderNoResults();
    }
    return <LeasesTable />;
  }

  renderSales() {
    const { sales } = this.props;
    if (sales.length === 0) {
      return this.renderNoResults();
    }
    return <SalesTable />;
  }

  renderFilterTopBar() {
    const {
      currentTab,
      leases,
      meta,
      sales
    } = this.props;


    const pageData = {
      currentPage: meta.currentPage,
      itemCount: currentTab === 'lease' ? leases.length : sales.length,
      itemSingularLabel: 'comp',
      pageSize: meta.pageSize,
      totalCount: meta.totalCount
    };

    return (
      <CompsFilterTopbar pageData={pageData} />
    );
  }

  renderPagination() {
    const { currentTab, leases, sales, meta } = this.props;

    const comps = currentTab === 'lease' ? leases : sales;

    if (comps.length < meta.totalCount) {
      return (
        <Pagination
          currentPage={meta.currentPage}
          totalPages={meta.totalPages}
          onChange={this.handlePageChange}
        />
      );
    }
  }
}

const mapStateToProps = state => ({
  ...state.search,
  canExport: canExport(state),
  currentTab: getCurrentTab(state),
  currentView: getCurrentView(state),
  edgeCrm: state.features.edgeCrm,
  selectedLeaseIds: getSelectedLeaseIds(state),
  selectedSaleIds: getSelectedSaleIds(state)
});

const mapDispatchToProps = {
  downloadLeasesAsCSV,
  downloadSalesAsCSV,
  retrieveLeases,
  retrieveSales,

  onChangeLeasePage: changeLeasePage,
  onChangeSalePage: changeSalePage
};

export default connect(mapStateToProps, mapDispatchToProps)(CompsTab);
